/* eslint-disable @typescript-eslint/ban-ts-comment */
import { observable, action, computed, autorun } from 'mobx';
import JSBI from 'jsbi';
import { InhouseConnection, AgentChat } from '@shopee/shopee-chatbot-agent-chat';
import {
  ChannelType,
  UserSessionStatus,
} from '@shopee/shopee-chatbot-agent-chat/dist/types';
import { AxiosInstance } from 'axios';
import { browserInstance as browser } from '@/utils/browser';
import sessionIDListStore from './sessionIDList';
import initStore from './init';
import {
  SessionIDType,
  initTimeOut,
  AGENT_END_REASON,
  FIRE_FLAG,
  isIosReg,
  ReportLabel,
  LIVE_AGENT_DATA_KEY,
  TRACKING_OPERATION,
} from '@/utils/constants';
import {
  afterDialogueShow,
  handleInitBubble,
  handleHideBubble,
  sendAgentHeartbeat,
  getMessageContent,
  getDefaultMessageContent,
  getTrans,
} from '@/utils/tools';
import reporter from '@/utils/report';
import { addListener, removeMediasListener, updateMediasStatusWhenChatEnd } from '@/utils/agent';
import { updateBubbleStatus } from '@/utils/bridges/chat';
import { clearDataFromLocalStorage, saveDataToLocalStorage } from '@/utils/bridges/localStorage';
import { initStoreData } from '@/config';
import { getEmergencyTemplate } from '@/services/chat/index';
import { MediasIdMapPosition, UploadStatus } from '@/types/components/Video';
import { getConfig, UseLiveChatSDKApp, isBank } from '@/config/core';
import { getLiveChatSDKHttpHeaders, getSDKToken } from '@/utils/liveChatToken';
import { emergencyTemplateRs } from '@/utils/somePromise/emergencyTemplatePromise';
import { dispatchTrack } from '@/utils/tracking';
import { WEB_FORM_TYPE, PARTNER_OFFLINE_FROM_URL, getSuffix } from '@/utils/locale';
import userStore from './user';
import { LOYALTY_TIER } from './user/constants';
import { mdapResponseInterceptors } from '@/utils/mdap/reporter';
import { App, PartnerEntryPoint } from '@/config/constants';
import { UploadEnquiryInfoBizContent } from '@/types/history';
import { ProbingFormFile } from '@/components/ProbingFormUpload';
import { ENTRY_POINT } from '@/types/entryPoints';
import { getLiveChatApiDomain, isSaasEntryPoint } from '@/utils/saas';
import { DIALOGUE_FROM, DIALOGUE_TYPE } from '@/types/common';
import { addDialogue } from '@/utils/sessionUtils';
import React from 'react';
import sessionStore from './session';

const keyCache = new Map();

export interface AgentStoreData {
  sessionID: string;
  sessionKey: string;
  shopeeChatKey: string;
  inAgent: boolean;
  liveChatStatus: LiveChatStatus;
  position: number;
  initialPosition: number | null;
  queuePositionMax: number;
  onceInQueue: boolean;
  agentName: string;
  sequence: number;
  timeout: number;
  inQueue: boolean;
  userType: string;
  enquiryType: string;
  channelType: ChannelType;
  loadingInAgent: boolean;
  connectPremiumLiveChatDirectly: boolean;
  maxFileSize: number;
  maxImageSize: number;
  multipartUploadBlockSize: number;
  multipartUploadConcurrency: number;
  isShowCancelQueueButton?: boolean;
  isOfflineWebformSubmitted?: boolean;
  queueXnumber?: number;
  inboundOrderSn?: string;
  reasonCodeL1Id?: number;
  userTypeName?: string;
  userTypeLabel?: string;
}

interface AgentListItem {
  from: 'agent' | 'system' | 'user' | 'bot';
  type: string;
  data?: any; // TODO:定义类型
  _useAfterDialogueShow_?: boolean;
}

interface EmergencyTemplate {
  content: string;
  webFormButton: boolean;
  offlineFormUrl?: string;
  webFormPlatform?: ChannelType;
}

export interface AgentData {
  [propsName: string]: AgentListItem[];
}

export enum UploadFileStatus {
  UPLOADING = 'UPLOADING',
  UPLOADED = 'UPLOADED',
}

export enum LiveChatStatus {
  none = 0,
  created,
  inQueue,
  inChat,
  end,
}

const { userAgent } = window.navigator;
const isIos = isIosReg.test(userAgent);

const ENTRY_POINTS_HAVING_INHOUSE_WEB_FORM = [
  ENTRY_POINT.mePage,
  ENTRY_POINT.mePagePremium,
  ENTRY_POINT.helpCentre,
  ENTRY_POINT.helpCentrePremium,
  ENTRY_POINT.sellingPage,
  ENTRY_POINT.shippingPage,
  ENTRY_POINT.MitraPartner,
  ENTRY_POINT.ShopeeFoodPartner,
  ENTRY_POINT.ShopeePayPartner,
  ENTRY_POINT.MystockPartner,
  ENTRY_POINT.RRDetailsPage,
  ENTRY_POINT.RRShippingPage,
  ENTRY_POINT.RRDetailsPagePremium,
  ENTRY_POINT.RRShippingPagePremium,
  ENTRY_POINT.OrderDetailPage,
  ENTRY_POINT.shopeeFoodHC,
] as number[];

function passGatewayConfig() {
  const curHostName = window.location.hostname;
  if (isBank()) {
    return {
      http: curHostName,
      websocket: curHostName,
      bff: curHostName,
    };
  } else {
    return {
      http: getLiveChatApiDomain() || curHostName,
      websocket: getLiveChatApiDomain() || curHostName,
    };
  }
}

function getAgentChatParams({ channelType }: { channelType?: ChannelType }) {
  const defaultParams = {
    ...(getConfig()?.agentChatConstructorParams || {}),
    passGateway: passGatewayConfig(),
    fetchInjector: (fetch: AxiosInstance) => {
      fetch.interceptors.request.use((config) => {
        if (UseLiveChatSDKApp.is()) {
          const tokens = getLiveChatSDKHttpHeaders();

          Object.entries(tokens).map(([key, value]) => {
            config.headers.common[key] = value || '';
          });
        }
        return config;
      });
      fetch.interceptors.response.use((response) => {
        // @ts-ignore
        response?.response && mdapResponseInterceptors(response?.response);
        return response;
      });
    },
    wsPathInjector: (path: string) => {
      if (UseLiveChatSDKApp.is()) {
        const paramCharacter = path.includes('?') ? '&' : '?';
        return `${path}${paramCharacter}chat_access_token=${getSDKToken()?.chatAccessToken || ''}`;
      }

      return path;
    },
  } as any;

  if (channelType) {
    defaultParams.channelType = channelType;
  }

  return defaultParams;
}

export class AgentStore {
  @observable agentData: AgentData = {};

  @observable sessionID = '';

  @observable sessionKey = '';

  @observable shopeeChatKey = '';

  @observable liveChatStatus = LiveChatStatus.none;

  @observable inAgent: boolean = initStoreData.agent.inAgent ?? false;

  @observable onceInQueue = false;

  @observable hasEndLiveChat = false;

  @observable position = 1;

  @observable initialPosition: number | null = null;

  // 排队位置变更最大的数量值，只有位置小于该值才有排队变化消息
  @observable queuePositionMax = 0;

  @observable typingFlag = false;

  @observable agentName = '';

  @observable isOpenMore = false;

  @observable loadingInAgent = false;

  // 是否在进线前的获取信息阶段 SPCB-29309
  @observable inNewFlowBeforeAgent = false;

  // agentEnded与否及理由，0：未结束 1：正常结束 2：异常结束
  @observable agentEndedReason: number = AGENT_END_REASON.NOT_END;

  @observable timeout: number = initTimeOut;

  @observable sequence: number | string = 0;

  // 是否弹过rating
  @observable hasShowRating = false;

  // 标识getChatHistory请求中
  // @observable isLoadingHistory: boolean;

  // getChatHistory请求中，用来临时保存getMessage返回的消息
  // @observable tempStoreWhenLoadingHistory: {
  //   data: ChatResData;
  //   key: string;
  // }[];

  @observable inQueue = false;

  @observable cancelGetMsg: any = null;

  // 频控用 - 请求时间戳
  @observable requestTimestamp = 0;

  // 延迟图片发送至chat_history返回后
  // 解决：上传图片失败导致前端中断轮询
  @observable fireStore: any = { flag: isIos ? FIRE_FLAG.NOT_REQUEST : FIRE_FLAG.FINISH };

  // 种时间戳的
  @observable showTimestamp: number = new Date().getTime();

  @observable restorePromise?: Promise<any>;

  @observable restoreResolve?: Function;

  userType: number | string = '';

  userTypeName = '';

  userTypeLabel = '';

  enquiryType: number | string = '';

  // IH 使用
  inhouseEnquiryTypeName = '';

  // 进线前 inbound order 使用
  isCanNotFindOrder = false;
  inboundOrderSn = '';

  // 用来保存 inhouse 的队列名；SF 的队列名直接保存到 enquiryType 了
  selectEnquiry = '';
  @observable channelType!: ChannelType;

  @observable maxFileSize = 41943040;
  @observable maxImageSize = 2097152;
  @observable multipartUploadBlockSize = 104857600; // 100m，fileproxy目前单次最大支持上传100m文件
  @observable multipartUploadConcurrency = 3;

  // 上传中的文件数量 若为0 则已经全部上传完成
  @observable fileUploadingCount = 0;

  // 初始化session错误
  @observable initSessionError = false;

  @observable connection!: InhouseConnection;

  @observable connectionConfigInfo = { queuePositionUpdateSwitchOpen: true };

  // 2023.2.08 增加apiBaseUrl AND isSupportFloatingBubble
  apiBaseUrl = location.origin;
  isSupportFloatingBubble = true; // Native是否支持common set，导致是否展示FloatingBubble ，如果不支持不调用floating bubble
  isSupportCsat = true; // Native是否支持common set，导致是否展示Csat ，如果不支持不调用csat
  chat = new AgentChat(getAgentChatParams({}));

  // 默认的emergency template (人工客服聊天的默认错误提示模版)
  @observable defaultEmergencyTemplate: EmergencyTemplate = {
    content: '',
    webFormButton: false,
  };

  webFormUuid = '';

  // 视频对应的气泡位置
  @observable mediasIdMapPosition: MediasIdMapPosition = {};

  // 是否是直接连接live chat
  @observable connectPremiumLiveChatDirectly = false;

  // 是否断开连接
  @observable isDisconnected = false;

  @observable hasUnreadMsgWhenPageInactive = false;

  // overflow management toggle and X number
  @observable isShowCancelQueueButton?: boolean;
  @observable queueXnumber?: number;

  // overflow management offline webform submitted state
  @observable isOfflineWebformSubmitted = false;
  @observable autoQueueEntryPoint?: ENTRY_POINT;

  @observable showContactMessage = false;

  @observable problemType = 0;
  @observable problemTypeName = '';
  @observable description = '';
  @observable files: ProbingFormFile[] = [];

  @observable reasonCodeL1Id?: number;
  @observable reasonCodeL1Name?: string;

  @observable userExistCase?: boolean;
  @observable caseId?: string;
  @observable firstEnquiryTime?: number;
  @observable caseUpdateTime?: number;
  @observable orderItemName?: string;
  @observable orderItemFirstImageUrl?: string;
  @observable reasonCode?: number;
  @observable orderSN?: string;

  constructor(initData: { inAgent: boolean }) {
    const isSaas = isSaasEntryPoint();
    const appVersion = browser.appVersion || 0;
    const liveChatSdkVersion = browser.liveChatSdkVersion || 0;
    const ShopeeAppNativeSupportCommonSetAppVersion = 29900; // TODO 2023.2.14  暂定2.99版本修复，后续确认修复版本后再进行修改
    const LiveChatSdkSupportCommonSetAppVersion = 10002; // TODO 2023.2.14   暂定LiveChatSDk 10002 版本修复，后续确认修复版本后再进行修改
    // driver ,food app 旧版本下隐藏floating bubble
    if (browser.isLiveChatSDKApp && isSaas && liveChatSdkVersion < LiveChatSdkSupportCommonSetAppVersion) {
      this.isSupportFloatingBubble = false;
      return;
    }
    if (browser.shopeeApp && isSaas && appVersion < ShopeeAppNativeSupportCommonSetAppVersion) {
      this.isSupportFloatingBubble = false;
      if (browser.ios) {
        // ios隐藏csat
        this.isSupportCsat = false;
      }
      return;
    }
    this.apiBaseUrl = `https://${getLiveChatApiDomain()}`;
  }

  @computed
  get ifRequestOverfast() {
    if (this.requestTimestamp) {
      const now = new Date().getTime();
      return now - this.requestTimestamp < 1000 / 3;
    } else {
      return false;
    }
  }

  @computed
  get enquiryTypeName() {
    return this?.chat?.channelType === ChannelType.inhouse ? this.selectEnquiry : this.enquiryType;
  }

  @computed
  get isLiveChatCreated() {
    return this.liveChatStatus !== LiveChatStatus.none;
  }

  @action
  update(data: {
    maxFileSize: number;
    maxImageSize: number;
    multipartUploadBlockSize: number;
    multipartUploadConcurrency: number;
    isShowCancelQueueButton?: boolean;
    queueXnumber?: number;
    queuePositionMax?: number;
  }) {
    const {
      maxFileSize,
      maxImageSize,
      multipartUploadBlockSize,
      multipartUploadConcurrency,
      isShowCancelQueueButton,
      queueXnumber,
      queuePositionMax,
    } = data;
    this.maxFileSize = maxFileSize || this.maxFileSize;
    this.maxImageSize = maxImageSize || this.maxImageSize;
    this.multipartUploadBlockSize = multipartUploadBlockSize || this.multipartUploadBlockSize;
    this.multipartUploadConcurrency = multipartUploadConcurrency || this.multipartUploadConcurrency;
    this.isShowCancelQueueButton = isShowCancelQueueButton || this.isShowCancelQueueButton;
    this.queueXnumber = queueXnumber || this.queueXnumber;
    this.queuePositionMax = queuePositionMax || this.queuePositionMax;
  }

  @action
  ADD_AGENT_DATA_ITEM(dataItem: AgentListItem[] | AgentListItem, targetSessionId: string = this.sessionID) {
    if (Array.isArray(dataItem)) {
      dataItem.forEach((item) => this.ADD_AGENT_DATA_ITEM(item, targetSessionId));
    } else {
      const key = dataItem?.data?.id;
      if (key && keyCache.get(key)) {
        console.warn('duplicate message id: ', key);
        reporter.apiBatchReport({ type: 'count', value: { label: 'live_chat_duplicate_message_id' }});
        reporter.apiLog('Live chat duplicate message id', { level: 'debug', id: key });
        return;
      }

      keyCache.set(key, 1);
      if (!this.agentData[targetSessionId]) {
        this.agentData = {
          ...this.agentData,
          [targetSessionId]: [],
        };
      }

      let currentAgentData = this.agentData[targetSessionId];
      const currentTime = new Date().getTime();

      // >5min 回显时间戳，前提：非system消息
      if (dataItem.from !== 'system' && currentTime - this.showTimestamp >= 300000) {
        this.ADD_TIMESTAMP(currentAgentData);
      }

      // 发送1s后仍无成功则显示loading态
      if (dataItem.data?.requesting) {
        const curPos = currentAgentData.length;
        setTimeout(() => {
          if (currentAgentData[curPos]?.data?.requesting) {
            this.UPDATE_AGENT_DATA_ITEM({ loading: true }, curPos);
          }
        }, 1000);
      }

      // inhouse 接口且存在 seq id 时才需要排序
      // 寻找当前列表中是否存在 seq id 比当前 id 还大的情况,如果有则说明出现乱序
      if (this?.chat?.channelType === ChannelType.inhouse && dataItem.data?.id) {
        const index = currentAgentData.findIndex((curItem) => {
          if (!curItem.data?.id) {
            return false;
          }
          // 如果 curItem 的 id 大于 传进来的 dataItem 的 id, 说明有乱序
          return JSBI.greaterThan(JSBI.BigInt(curItem.data?.id), JSBI.BigInt(dataItem.data?.id));
        });
        if (index !== -1) {
          currentAgentData = [
            ...currentAgentData.slice(0, index),
            dataItem,
            ...currentAgentData.slice(index, currentAgentData.length),
          ];
          afterDialogueShow();
          return currentAgentData.length - 1;
        }
      }

      if (dataItem.from !== 'user' && initStore.isScrollMaxThanOneScreen) {
        initStore.UPDATE_LAST_MESSAGE_CONTENT(
          // @ts-ignore
          getMessageContent[dataItem.type]?.(dataItem.data) || getDefaultMessageContent(),
        );
      }

      currentAgentData.push(dataItem);

      if (dataItem._useAfterDialogueShow_ !== false) {
        afterDialogueShow(dataItem.from);
      }

      return currentAgentData.length - 1;
    }
  }

  @action
  UPDATE_AGENT_DATA_ITEM(obj: any, index: number) {
    const { sessionID } = this;
    if (!this.agentData[sessionID]) {
      this.agentData = {
        ...this.agentData,
        [sessionID]: [],
      };
    }

    const currentAgentData = this.agentData[sessionID];
    const currentAgentItem = currentAgentData[index ?? currentAgentData.length - 1];

    currentAgentItem.data = {
      ...currentAgentItem.data,
      ...obj,
    };
  }

  @action
  GET_AGENT_DATA_ITEM(index: number) {
    const { sessionID } = this;

    if (!this.agentData[sessionID]) {
      this.agentData = {
        ...this.agentData,
        [sessionID]: [],
      };
    }

    const currentAgentData = this.agentData[sessionID];
    const currentAgentItem = currentAgentData[index || currentAgentData.length - 1];

    return currentAgentItem;
  }

  @action
  UPDATE_SESSION_ID(id: string) {
    this.sessionID = id;

    sessionIDListStore.UPDATE_SESSION_ID_LIST({
      type: SessionIDType.AGENT,
      id,
    });
  }

  @action
  UPDATE_SESSION_KEY(key: string) {
    this.sessionKey = key;
  }

  @action
  UPDATE_CHAT_KEY(key: string) {
    this.shopeeChatKey = key;
  }

  @action
  UPDATE_CHAT_STATUS(liveChatStatus: LiveChatStatus) {
    this.liveChatStatus = liveChatStatus;
  }

  @action
  START_AGENT() {
    if (this.inAgent) {
      return;
    }
    this.inAgent = true;
    this.FINISH_LOADING_IN_AGENT();
    this.QUEUE_START();

    // 进入agent，发起心跳
    sendAgentHeartbeat(true);

    // 开启时间戳
    this.showTimestamp = 0;
  }

  @action
  END_AGENT(endType: number = AGENT_END_REASON.NORMAL) {
    this.NOT_TYPING();
    // CB单向进入agent，不设回退
    this.agentEndedReason = endType;
    this.FINISH_LOADING_IN_AGENT();
    this.QUEUE_END();

    // 结束 live agent 时清空 userType, enquiryType
    this.UPDATE_AGENT_CASE_FIELD();
    this.liveChatStatus = LiveChatStatus.end;
    // init Native 数据
    handleInitBubble();
    this.noticeNativeWhenAgentEnded();
    // 取消所有上传中的media
    updateMediasStatusWhenChatEnd(true);
    // 取消对于medias状态的监听
    removeMediasListener();
  }

  @action
  END_AGENT_BY_CANCEL_QUEUE() {
    agentStore.END_AGENT(AGENT_END_REASON.USER_CANCEL_QUEUE);

    initStore.UPDATE_cancelFromAgent();

    // 返回到ChatBot状态
    this.inAgent = false;
    this.liveChatStatus = LiveChatStatus.none;
    this.initialPosition = null;
    this.agentEndedReason = AGENT_END_REASON.NOT_END;
  }

  @action
  END_AGENT_BY_QUEUE_FILTER_TIMEOUT() {
    initStore.UPDATE_cancelFromAgent();

    // 返回到ChatBot状态
    this.inAgent = false;
    this.liveChatStatus = LiveChatStatus.none;
    this.initialPosition = null;
    this.agentEndedReason = AGENT_END_REASON.NOT_END;
  }

  @action
  END_AGENT_BY_CAN_CONTINUE() {
    initStore.UPDATE_cancelFromAgent();

    // 返回到ChatBot状态
    this.inAgent = false;
    this.liveChatStatus = LiveChatStatus.none;
    this.initialPosition = null;
    this.agentEndedReason = AGENT_END_REASON.NOT_END;
    // 标记live chat session已结束
    this.UPDATE_HAS_END_LIVECHAT(true);
  }

  noticeNativeWhenAgentEnded() {
    updateBubbleStatus({ chat_status: UserSessionStatus.ended, show: false });
  }

  @action
  UPDATE_POSITION(position: number) {
    this.position = position;

    // 超过5s没接入客服，上报
    if (position === 0) {
      setTimeout(() => {
        if (this.inQueue) {
          reporter.apiBatchReport({ type: 'count', value: { label: ReportLabel.AgentError.WaitingTooLong }});
          reporter.apiLog('More than 5s', { level: 'warn' });
        }
      }, 5000);
    }
  }

  @action
  UPDATE_ONCE_IN_QUEUE() {
    this.onceInQueue = true;
  }

  @action
    UPDATE_HAS_END_LIVECHAT = (hasEndLiveChat = false) => {
      this.hasEndLiveChat = hasEndLiveChat;
    };

  @action
  UPDATE_INITIAL_POSITION(position: number) {
    this.initialPosition = position;
    const { initialPosition, queueXnumber = 0 } = this;
    const is_greater_than_threshold = (initialPosition as number) > (queueXnumber as number) ? 1 : 0;

    dispatchTrack({
      operation: TRACKING_OPERATION.ACTION_LIVEAGENT_ENTER_QUEUE,
      data: {
        initial_queue_number: initialPosition,
        chat_session_id: initStore.sessionId,
        is_greater_than_threshold: is_greater_than_threshold,
      },
    });
  }

  @action
  TYPING(useAfterDiagueShow = true) {
    const dialogueFrom = 'agent';
    this.typingFlag = true;

    if (useAfterDiagueShow) {
      afterDialogueShow(dialogueFrom); // 展示typing态
    }
  }

  @action
  NOT_TYPING() {
    this.typingFlag = false;
  }

  @action
  UPDATE_AGENT_NAME(agentName: string) {
    this.agentName = agentName;
  }

  @action
  TOGGLE_OPEN_MORE(flag?: boolean) {
    if (flag !== undefined) {
      this.isOpenMore = flag;
    } else {
      this.isOpenMore = !this.isOpenMore;
    }
  }

  @action
  RECOVER_AGENT(data: AgentStoreData) {
    this.sessionID = data.sessionID;
    this.sessionKey = data.sessionKey;
    this.shopeeChatKey = data.shopeeChatKey;
    this.inAgent = data.inAgent;
    this.liveChatStatus = data.liveChatStatus;
    this.position = data.position;
    this.initialPosition = data.initialPosition;
    this.queuePositionMax = data.queuePositionMax;
    this.agentName = data.agentName;
    this.sequence = data.sequence;
    this.timeout = data.timeout;
    this.inQueue = data.inQueue;
    this.showTimestamp = 0; // 开启时间戳
    this.userType = data.userType; // 开启时间戳
    this.channelType = data.channelType; // 开启时间戳
    this.connectPremiumLiveChatDirectly = data.connectPremiumLiveChatDirectly;
    this.maxFileSize = data.maxFileSize;
    this.maxImageSize = data.maxImageSize;
    this.multipartUploadBlockSize = data.multipartUploadBlockSize;
    this.multipartUploadConcurrency = data.multipartUploadConcurrency;
    this.isShowCancelQueueButton = data.isShowCancelQueueButton;
    this.queueXnumber = data.queueXnumber;
    this.enquiryType = data.enquiryType;
    this.inboundOrderSn = data.inboundOrderSn ?? '';
    this.reasonCodeL1Id = data.reasonCodeL1Id;
    this.userTypeName = data.userTypeName ?? '';
    this.userTypeLabel = data.userTypeLabel ?? '';

    if (data.inAgent) {
      // 恢复agent，发起心跳
      sendAgentHeartbeat(true);
    }

    if (data.liveChatStatus === LiveChatStatus.created) {
      this.LOADING_IN_AGENT();
    }
  }

  @action
  UPDATE_SEQ(sequence: number | string) {
    this.sequence = sequence;
  }

  @action
  LOADING_IN_AGENT() {
    this.loadingInAgent = true;
  }

  @action
  FINISH_LOADING_IN_AGENT() {
    this.loadingInAgent = false;
  }

  @action
  SET_TIMEOUT(timeout: number) {
    this.timeout = timeout * 1000;
  }

  // @action
  // TOGGLE_LOADING_HISTORY(flag?: boolean) {
  //   if (flag !== undefined) {
  //     this.isLoadingHistory = flag;
  //   } else {
  //     this.isLoadingHistory = !this.isLoadingHistory;
  //   }
  // }

  // @action
  // ADD_tempStoreWhenLoadingHistory(data: ChatResData, key: string) {
  //   this.tempStoreWhenLoadingHistory.push({ data, key });
  // }

  // @action
  // EMPTY_tempStoreWhenLoadingHistory() {
  //   this.tempStoreWhenLoadingHistory = [];
  // }

  @action
  TOGGLE_SHOW_RATING() {
    this.hasShowRating = true;
  }

  @action
  CLOSE_SHOW_RATING() {
    this.hasShowRating = false;
  }

  // Queue
  @action
  QUEUE_START() {
    this.inQueue = true;
  }

  @action
  QUEUE_END() {
    this.inQueue = false;
  }

  // @action
  // UPDATE_cancelGetMsg(data: any) {
  //   this.cancelGetMsg = data;
  // }

  @action
  UPDATE_REQUEST_TIMESTAMP() {
    this.requestTimestamp = new Date().getTime();
  }

  @action
  UPDATE_FIRE_STORE(flag: any) {
    this.fireStore = {
      ...this.fireStore,
      ...flag,
    };
  }

  @action
  UPDATE_showTimestamp(time: number) {
    this.showTimestamp = time;
  }

  @action
  ADD_TIMESTAMP(currentAgentData: AgentListItem[]) {
    const currentTime = new Date().getTime();
    currentAgentData.push({
      from: 'system',
      type: 'AgentTime',
      data: { time: currentTime },
    });
    this.UPDATE_showTimestamp(currentTime);
  }

  @action
  UPDATE_SINGLE_AGENT_CASE_FIELD(
    fieldName:
    'userType' | 'enquiryType' | 'userTypeName' | 'inhouseEnquiryTypeName' | 'isCanNotFindOrder'
    | 'inboundOrderSn' | 'problemType' | 'problemTypeName' | 'description' | 'files' | 'userTypeLabel',
    fieldValue: any,
  ) {
    // @ts-ignore;
    this[fieldName] = fieldValue;
  }

  @action
  UPDATE_IN_BEFORE_AGENT(payload: boolean) {
    this.inNewFlowBeforeAgent = payload;
    if (!payload) {
      sessionStore.UPDATE_DIALOGUE_WHO_HAS_CHANNEL_OPTION({ disableButtons: true });
    }
  }

  @action
  async UPDATE_AGENT_CASE_FIELD({
    userType = '',
    enquiryType = '',
    userTypeName = '',
    userTypeLabel = '',
    inhouseEnquiryTypeName = '',
    isCanNotFindOrder = false,
    inboundOrderSn = '',
    problemType = 0,
    problemTypeName = '',
    description = '',
    files = [],
    reasonCodeL1Id,
    reasonCodeL1Name,
    userExistCase,
    caseId = '',
    firstEnquiryTime = 0,
    caseUpdateTime = 0,
    reasonCode = 0,
    orderSN = '',
    orderItemName = '',
    orderItemFirstImageUrl = '',
  }: {
    userType?: number | string;
    enquiryType?: number | string;
    userTypeName?: string;
    userTypeLabel?: string;
    inhouseEnquiryTypeName?: string;
    isCanNotFindOrder?: boolean;
    inboundOrderSn?: string;
    problemType?: number;
    problemTypeName?: string;
    description?: string;
    files?: ProbingFormFile[];
    reasonCodeL1Id?: number;
    reasonCodeL1Name?: string;
    userExistCase?: boolean;
    caseId?: string;
    firstEnquiryTime?: number;
    caseUpdateTime?: number;
    reasonCode?: number;
    orderSN?: string;
    orderItemName?: string;
    orderItemFirstImageUrl?: string;
  } = {}) {
    this.userType = userType;
    this.enquiryType = enquiryType;
    this.userTypeName = userTypeName;
    this.userTypeLabel = userTypeLabel;
    this.inhouseEnquiryTypeName = inhouseEnquiryTypeName;
    this.isCanNotFindOrder = isCanNotFindOrder;
    this.inboundOrderSn = inboundOrderSn;

    this.problemType = problemType;
    this.problemTypeName = problemTypeName;
    this.description = description;
    this.files = files;

    this.reasonCodeL1Id = reasonCodeL1Id;
    this.reasonCodeL1Name = reasonCodeL1Name;

    this.userExistCase = userExistCase ?? this.userExistCase ?? false;
    this.caseId = caseId;
    this.firstEnquiryTime = firstEnquiryTime;
    this.caseUpdateTime = caseUpdateTime;

    this.reasonCode = reasonCode;
    this.orderSN = orderSN;

    this.orderItemName = orderItemName;
    this.orderItemFirstImageUrl = orderItemFirstImageUrl;
  }

  @action
    updateInboundOrderSn = (inboundOrderSn: string) => {
      this.inboundOrderSn = inboundOrderSn;
    };

  @action
  UPDATE_FILE_UPLOADING_COUNT(status: UploadFileStatus) {
    if (status === UploadFileStatus.UPLOADING) {
      // 新增上传 则加1
      this.fileUploadingCount += 1;
    } else {
      // 结束上传 则减1
      this.fileUploadingCount -= 1;
    }
  }

  @action
  UPDATE_INIT_SESSION_ERROR(initSessionError: boolean) {
    this.initSessionError = initSessionError;
  }

  @action
  updateConnection(connection: InhouseConnection) {
    this.connection = connection;
  }

  @action
  updateConnectionConfigInfo(configInfo: { queuePositionUpdateSwitchOpen: boolean }) {
    this.connectionConfigInfo = configInfo;
  }

  async initConnection(
    data?: UploadEnquiryInfoBizContent,
  ) {
    if (!data) {
      console.warn('Enquiry data is required when init connect');
      return;
    }

    const connection = await this.chat.connect(data);
    this.updateConnection(connection);
    agentStore.CLOSE_SHOW_RATING(); // 重新进线后重置hasShowRating为false
    addListener(this.connection);
    this.restoreResolve?.();
    return this.connection;
  }

  async restoreConnection(data: {
    id: string;
    key: string;
    timeout: number;
    shopeeChatKey: string;
    sequence: number;
    userType: number | string;
    channelType: ChannelType;
  }) {
    // this.connection 可能会恢复失败
    if (data.channelType === ChannelType.salesforce && data.sequence === 0) {
      data.sequence = -1; // salesforce初始值为0会导致漏掉第一条消息
    }

    const connection = await this.chat.restore(data);
    this.updateConnection(connection!);
    addListener(this.connection);
    this.restoreResolve?.();
    return this.connection;
  }

  @action
  UPDATE_EMERGENCY_TEMPLATE(template: EmergencyTemplate) {
    this.defaultEmergencyTemplate = template;
  }

  @action
  async INIT_EMERGENCY_TEMPLATE() {
    const { emergencyTemplateParams, liveDefaultEmergencyTemplate, entryPoint, app } = getConfig();
    const defaultObj: EmergencyTemplate = {
      content: getTrans(liveDefaultEmergencyTemplate),
      webFormButton: false,
    };
    // partner不走接口获取紧急模版，固定紧急模版和webform link
    if (app === App.Partner) {
      const { INHOUSE_WEB_FORM_MAP } = await import('@/utils/webform');
      const inhouseWebFormUrl = (PARTNER_OFFLINE_FROM_URL?.[entryPoint as Exclude<PartnerEntryPoint, PartnerEntryPoint.MitraPartner>]?.url) || (INHOUSE_WEB_FORM_MAP[
        userStore.loyaltyTierCode === LOYALTY_TIER.TierPremium ? WEB_FORM_TYPE.Premium : WEB_FORM_TYPE.Normal
      ]?.url);
      defaultObj.webFormButton = true;
      defaultObj.offlineFormUrl = inhouseWebFormUrl;
      defaultObj.webFormPlatform = ChannelType.inhouse;
      this.UPDATE_EMERGENCY_TEMPLATE(defaultObj);
      emergencyTemplateRs();
      return;
    }
    await userStore.userInfoPromise;

    const { INHOUSE_WEB_FORM_MAP } = await import('@/utils/webform');
    // 接口失败使用 IH 表单兜底（如果有的话）
    const inhouseWebFormUrl
      = INHOUSE_WEB_FORM_MAP[
        userStore.loyaltyTierCode === LOYALTY_TIER.TierPremium ? WEB_FORM_TYPE.Premium : WEB_FORM_TYPE.Normal
      ]?.url;
    const isInhouseWebFormUrlAvailable = ENTRY_POINTS_HAVING_INHOUSE_WEB_FORM.includes(entryPoint) && inhouseWebFormUrl;
    try {
      const res = await getEmergencyTemplate(emergencyTemplateParams);
      if (res?.code === 0) {
        defaultObj.webFormPlatform = res?.data?.web_form_platform;

        if (res?.data?.web_form_url) {
          defaultObj.offlineFormUrl = res.data.web_form_url;
        } else if (defaultObj.webFormPlatform === ChannelType.inhouse && isInhouseWebFormUrlAvailable) {
          defaultObj.offlineFormUrl = inhouseWebFormUrl;
        }

        if (res?.data?.emergency_template?.is_online) {
          const obj = Object.assign(defaultObj, {
            content: res.data.emergency_template.content,
            webFormButton: res.data.emergency_template.show_web_button,
          });
          this.UPDATE_EMERGENCY_TEMPLATE(obj);
          emergencyTemplateRs();
          return;
        }
      } else if (isInhouseWebFormUrlAvailable) {
        defaultObj.offlineFormUrl = inhouseWebFormUrl;
        defaultObj.webFormPlatform = ChannelType.inhouse;
      } else {
        defaultObj.webFormPlatform = ChannelType.salesforce;
      }

      this.UPDATE_EMERGENCY_TEMPLATE(defaultObj);
      emergencyTemplateRs();
    } catch (err) {
      if (isInhouseWebFormUrlAvailable) {
        defaultObj.offlineFormUrl = inhouseWebFormUrl;
        defaultObj.webFormPlatform = ChannelType.inhouse;
      } else {
        defaultObj.webFormPlatform = ChannelType.salesforce;
      }

      this.UPDATE_EMERGENCY_TEMPLATE(defaultObj);
      emergencyTemplateRs();
    }
  }

  @action
  updateMediasIdMapPosition(id: string, position: number) {
    this.mediasIdMapPosition = {
      ...this.mediasIdMapPosition,
      [id]: position,
    };
  }

  @action
  updateConnectPremiumLiveChatDirectly(connectPremiumLiveChatDirectly: boolean) {
    this.connectPremiumLiveChatDirectly = connectPremiumLiveChatDirectly;
  }

  @action
  updateIsDisconnected(value: boolean) {
    this.isDisconnected = value;
  }

  @action
  updateSelectEnquiry(value: string) {
    this.selectEnquiry = value;
  }

  @action
  updateHasUnreadMsgWhenPageInactive(hasUnreadMsgWhenPageInactive: boolean) {
    this.hasUnreadMsgWhenPageInactive = hasUnreadMsgWhenPageInactive;
  }

  @action
  update_agentEndedReason(code: number) {
    this.agentEndedReason = code;
  }

  @action
  updateIsOfflineWebformSubmitted(code: boolean) {
    this.isOfflineWebformSubmitted = code;
  }

  @action
  updateWebFormUuid(uuid: string) {
    this.webFormUuid = uuid;
  }

  @action
  updateChatChannelType(channelType?: ChannelType) {
    this.chat = new AgentChat(getAgentChatParams({ channelType }));
  }

  @action
  updateAutoQueueEntryPoint(value?: ENTRY_POINT) {
    this.autoQueueEntryPoint = value;
  }

  @action
  updateShowContactMessage(value: boolean) {
    this.showContactMessage = value;
  }

  @action
  restorePromisePending() {
    this.restoreResolve?.();
    this.restorePromise = new Promise(resolve => {
      this.restoreResolve = resolve;
    });
  }

  @action
  addDialogueMessage(children: any) {
    addDialogue(
      {
        // dialogueId: dialogueId,
        // dialogueType: dialogueType,
        from: DIALOGUE_FROM.BOT,
        type: DIALOGUE_TYPE.TFEMessage,
        data: {
          content: 'Which query method do you prefer?',
          buttonGroup: {
            buttons: [
              { children: React.createElement('div', null, 'test') },
            ],
          },
        },
      },
      false,
    );
  }

  // [live-chat-split-change]
  @action
  UPDATE_IN_AGENT(payload: boolean) {
    this.inAgent = payload;
  }
}

const agentStore = new AgentStore(initStoreData.agent);

export default agentStore;

// [live-chat-split-change]
// const disposer = autorun(saveAgentData, { delay: 0 });

export function saveAgentData(needSave = false) {
  if ((agentStore.liveChatStatus === LiveChatStatus.none) && !needSave) {
    return;
  }

  let agentHistory = agentStore.agentData[agentStore.sessionID];

  // 过滤
  agentHistory = agentHistory
    ?.filter((data) => {
      // 未读消息数量的提示
      const isUnreadMessages = data.type === 'UnreadMessages';
      // 未成功的Image
      const isFailImage = data.type === 'Image' && !data.data.imgUrl;
      // 未成功的File
      const isFailFile = data.type === 'FileUpload' && !data.data.fileUrl;
      // 未成功的video
      const isFailVideo = data.type === 'Video' && data.data.status !== UploadStatus.SendMessageSucceed;

      return !isUnreadMessages && !isFailImage && !isFailFile && !isFailVideo;
    })
    .filter((data: any, index, origin) => {
      // 为什么过滤两次 对于AgentTime来说 在第一次过滤中合法 但是其后有不合法的被过滤掉 导致AgentTime不合法
      // 如果相邻的两个都是时间 保留后一个
      const isNextItemAgentTime = data.type === 'AgentTime' && data[index + 1]?.type === 'AgentTime';
      // 最后一个不能是AgentTime
      const isLastItemAgentTime = index === origin.length - 1 && data.type === 'AgentTime';

      return !isNextItemAgentTime && !isLastItemAgentTime;
    });

  const info = {
    agentHistory: { [agentStore.sessionID]: agentHistory },
    agentStoreData: {
      sessionID: agentStore.sessionID,
      sessionKey: agentStore.sessionKey,
      shopeeChatKey: agentStore.shopeeChatKey,
      inAgent: agentStore.inAgent,
      liveChatStatus: agentStore.liveChatStatus,
      position: agentStore.position,
      initialPosition: agentStore.initialPosition,
      queuePositionMax: agentStore.queuePositionMax,
      agentName: agentStore.agentName,
      sequence: agentStore.sequence,
      timeout: agentStore.timeout,
      inQueue: agentStore.inQueue,
      userType: agentStore.userType,
      channelType: agentStore?.channelType || agentStore?.chat?.channelType,
      connectPremiumLiveChatDirectly: agentStore.connectPremiumLiveChatDirectly,
      maxFileSize: agentStore.maxFileSize,
      maxImageSize: agentStore.maxImageSize,
      multipartUploadBlockSize: agentStore.multipartUploadBlockSize,
      multipartUploadConcurrency: agentStore.multipartUploadConcurrency,
      isShowCancelQueueButton: agentStore.isShowCancelQueueButton,
      isOfflineWebformSubmitted: agentStore.isOfflineWebformSubmitted,
      queueXnumber: agentStore.queueXnumber,
      enquiryType: agentStore.enquiryType,
      inboundOrderSn: agentStore.inboundOrderSn,
      reasonCodeL1Id: agentStore.reasonCodeL1Id,
      userTypeName: agentStore.userTypeName,
      userTypeLabel: agentStore.userTypeLabel,
    },
  };

  try {
    saveDataToLocalStorage(LIVE_AGENT_DATA_KEY, JSON.stringify(info));
  } catch (error) {
    clearDataFromLocalStorage(LIVE_AGENT_DATA_KEY);
    reporter.apiLog(`Set LIVE_AGENT_DATA_KEY localStorage error.${error}`, { level: 'warn' });
  }
}
